import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@neo/primitives/sheet'
import { Skeleton } from '@neo/primitives/skeleton'
import { Typography } from '@neo/primitives/typography'
import { ArrowLeft, Search } from 'lucide-react'
import { useState } from 'react'
import {
  useConnectIntegrationMutation,
  useExternalAppsV2Query,
} from 'v2/pages/settings/integrations/queries'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import type { RExternalApp, RExternalAppDetails } from 'v2/types/api/settings'
import { redirectToOAuth } from '../nango'
import { CustomMcpForm } from './custom-mcp-form'
import { IntegrationAppSelection } from './integration-app-selection'
import {
  IntegrationConnectionForm,
  type IntegrationFormValues,
} from './integration-connection-form'
import { hasVisibleConnectionFields } from './integration-form-utils'

interface IntegrationSideSheetProps {
  onClose: () => void
}

interface CatalogBodyProps {
  externalApps: RExternalApp | undefined
  isLoading: boolean
  error: unknown
  searchTerm: string
  showCustomMcp: boolean
  onAppClick: (app: RExternalAppDetails) => void
  onCustomMcpClick: () => void
}

/**
 * The catalog half of the sheet, with its three states made explicit.
 *
 * Previously this rendered `externalApps && <IntegrationAppSelection …>`, so both
 * "still loading" and "request failed" collapsed into an empty sheet under a live
 * search box — indistinguishable from "your org has no apps", and permanent on
 * failure.
 */
const CatalogBody = ({
  externalApps,
  isLoading,
  error,
  searchTerm,
  showCustomMcp,
  onAppClick,
  onCustomMcpClick,
}: CatalogBodyProps) => {
  if (isLoading)
    return (
      <div className="mt-4 space-y-2" data-testid="loader-integration-catalog">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={`integration-catalog-skeleton-${index}`} className="h-10 w-full" />
        ))}
      </div>
    )

  if (error || !externalApps)
    return (
      <div
        className="mt-6 flex flex-col items-center gap-2 text-center"
        data-testid="error-integration-catalog"
      >
        <Typography variant="P/500">Could not load the app catalog.</Typography>
        <Typography variant="XS/400" className="text-t3">
          Close this panel and try again.
        </Typography>
      </div>
    )

  return (
    <IntegrationAppSelection
      externalApps={externalApps}
      searchTerm={searchTerm}
      showCustomMcp={showCustomMcp}
      onAppClick={onAppClick}
      onCustomMcpClick={onCustomMcpClick}
    />
  )
}

export const IntegrationSideSheet = ({ onClose }: IntegrationSideSheetProps) => {
  const { useAciIntegrations, isResolved: isRolloutResolved } = useIntegrationRollout()
  const {
    data: externalApps,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useExternalAppsV2Query()
  const { mutate: connectIntegration, isPending: isConnecting } = useConnectIntegrationMutation()
  const [selectedApp, setSelectedApp] = useState<RExternalAppDetails | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [customMode, setCustomMode] = useState<'mcp' | null>(null)
  const handleSubmit = (values: IntegrationFormValues, app: RExternalAppDetails) => {
    // Until the rollout resolves we do not know WHICH service to connect against.
    // Submitting anyway makes the mutation throw "rollout is still loading", which
    // the toast then reports as "Could not start authentication via the bridge" —
    // a confident and entirely wrong diagnosis. Hold the click instead; the submit
    // button carries the same condition in its `loading` prop below, so the user
    // sees a spinner rather than a dead control.
    if (isConnecting || !isRolloutResolved) return
    connectIntegration(
      { app, values },
      {
        onSuccess: (result) => {
          onClose()
          if (result.kind === 'redirect') redirectToOAuth(result.url)
        },
      },
    )
  }

  const handleAppClick = (app: RExternalAppDetails) => {
    if (!hasVisibleConnectionFields(app)) {
      handleSubmit({}, app)
      return
    }
    setSelectedApp(app)
  }

  const handleBackClick = () => {
    setSelectedApp(null)
    setCustomMode(null)
  }

  const isShowingDetail = Boolean(selectedApp || customMode)
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="flex w-[65%] flex-col sm:w-[65%] md:w-[65%] lg:max-w-[550px]">
        <SheetHeader>
          <SheetTitle className="flex items-center">
            {isShowingDetail ? (
              <div className="flex flex-row">
                <Button
                  variant="ghost"
                  tooltipContent="Back to integrations"
                  onClick={handleBackClick}
                  className="-ml-1 pl-0 hover:bg-transparent"
                  data-testid="button-back-to-app-list"
                >
                  <ArrowLeft className="h-6 w-6 text-secondary" />
                </Button>
                <div className="-ml-2 flex items-center">
                  <span className="text-h4-500">
                    {customMode === 'mcp'
                      ? 'Add a custom MCP server'
                      : `Integrate ${selectedApp?.name ?? ''}`}
                  </span>
                </div>
              </div>
            ) : (
              <Typography variant="H4/500">Integrate with your favorite tools</Typography>
            )}
          </SheetTitle>
        </SheetHeader>

        {!isShowingDetail && (
          <div className="px-1 pt-4">
            <Input
              placeholder="Search apps and tools"
              className="w-full"
              startIcon={Search}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              data-testid="input-search-apps"
            />
          </div>
        )}

        {customMode === 'mcp' ? (
          <CustomMcpForm onConnected={onClose} />
        ) : (
          <>
            {/* Zero-field apps submit straight from the catalog, where the
                footer button that surfaces `isConnecting` is not rendered. Dim
                and freeze the grid so the click reads as accepted. */}
            <div
              className={`flex-grow overflow-hidden ${
                isConnecting && !selectedApp ? 'pointer-events-none opacity-60' : ''
              }`}
              // Only the catalog grid is frozen while connecting (the freeze classes
              // above are likewise gated on !selectedApp). Announcing busy on the
              // form branch would tell screen readers the region is unavailable
              // while it is fully interactive.
              aria-busy={isConnecting && !selectedApp}
            >
              {selectedApp ? (
                <IntegrationConnectionForm
                  app={selectedApp}
                  onSubmit={(values) => handleSubmit(values, selectedApp)}
                />
              ) : (
                <CatalogBody
                  externalApps={externalApps}
                  isLoading={isCatalogLoading}
                  error={catalogError}
                  searchTerm={searchTerm}
                  showCustomMcp={useAciIntegrations}
                  onAppClick={handleAppClick}
                  onCustomMcpClick={() => setCustomMode('mcp')}
                />
              )}
            </div>
            {selectedApp && (
              <SheetFooter className="mt-6">
                <FormSubmitButton
                  formId="integration-external-app-form"
                  loading={isConnecting || !isRolloutResolved}
                  data-testid="button-integrate-app"
                >
                  {isConnecting ? 'Integrating...' : `Integrate ${selectedApp.name}`}
                </FormSubmitButton>
              </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
